import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FolderIcon, LinkIcon, BookmarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';

const SharedCollection = () => {
  const { shareToken } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      // Mock mode — show placeholder
      setCollection({
        name: 'Shared Collection',
        description: 'This is a mock shared collection.',
        ownerName: 'Alex',
        ownerProfileToken: 'mock-profile-token',
        bookmarkCount: 2,
        bookmarks: [
          { bookmarkId: '1', title: 'AWS Lambda', url: 'https://aws.amazon.com/lambda/', description: 'Serverless compute', tags: ['aws', 'serverless'], categories: ['Cloud', 'Infrastructure'], summary: 'AWS Lambda lets you run code without provisioning or managing servers. You pay only for the compute time you consume.' },
          { bookmarkId: '2', title: 'React', url: 'https://react.dev/', description: 'A JavaScript library for building UIs', tags: ['react', 'frontend'], categories: ['Web Development'] },
        ],
      });
      setLoading(false);
      return;
    }

    fetch(`${apiUrl}/collections/shared/${shareToken}`)
      .then(res => {
        if (res.status === 404) throw new Error('This collection is no longer shared or does not exist.');
        if (!res.ok) throw new Error('Failed to load collection. Please try again.');
        return res.json();
      })
      .then(data => { setCollection(data); })
      .catch(err => { setError(err.message); })
      .finally(() => setLoading(false));
  }, [shareToken]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Minimal header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BookmarkIcon className="h-6 w-6 text-primary-600" />
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">BookMarker</span>
          </Link>
          <Link to="/login" className="btn-primary text-sm">
            Sign in
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-3xl">
        {loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-20">
            <FolderIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">{error}</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Go to BookMarker
            </Link>
          </div>
        )}

        {!loading && collection && (
          <>
            {/* Collection header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <FolderIcon className="h-8 w-8 text-primary-500 flex-shrink-0" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{collection.name}</h1>
              </div>
              {collection.description && (
                <p className="text-gray-600 dark:text-gray-400 ml-11 text-base leading-relaxed">{collection.description}</p>
              )}
              <div className="ml-11 mt-2 flex items-center gap-3 flex-wrap">
                <p className="text-sm text-gray-400">
                  {collection.bookmarks.length === 1 ? '1 bookmark' : `${collection.bookmarks.length} bookmarks`}
                </p>
                {collection.ownerName && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <UserCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Curated by{' '}
                      {collection.ownerProfileToken ? (
                        <Link
                          to={`/u/${collection.ownerProfileToken}`}
                          className="font-medium text-primary-600 dark:text-primary-400 hover:underline ml-1"
                        >
                          {collection.ownerName}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-700 dark:text-gray-300 ml-1">{collection.ownerName}</span>
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Bookmarks */}
            {collection.bookmarks.length === 0 && (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                <p>This collection has no bookmarks yet.</p>
              </div>
            )}

            {collection.bookmarks.length > 0 && (
              <ul className="space-y-3">
                {collection.bookmarks.map((b, i) => (
                  <PublicBookmarkRow key={b.bookmarkId || i} bookmark={b} />
                ))}
              </ul>
            )}

            {/* Sign-up CTA */}
            <div className="mt-12 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-2xl px-6 py-8 text-center">
              <BookmarkIcon className="h-10 w-10 text-primary-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Save and organise your own bookmarks</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                BookMarker automatically extracts summaries, tags, and categories from every page you save.
              </p>
              <Link to="/login" className="btn-primary inline-block">
                Get started — it's free
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const PublicBookmarkRow = ({ bookmark }) => {
  const domain = (() => {
    try { return new URL(bookmark.url).hostname.replace(/^www\./, ''); }
    catch { return ''; }
  })();

  const hasTags = bookmark.tags && bookmark.tags.length > 0;
  const hasCategories = bookmark.categories && bookmark.categories.length > 0;

  return (
    <li className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-4 flex items-start gap-4 hover:shadow-sm transition-shadow">
      <LinkIcon className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <a
          href={bookmark.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors line-clamp-1"
        >
          {bookmark.title || bookmark.url}
        </a>
        {domain && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{domain}</p>}

        {bookmark.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">{bookmark.description}</p>
        )}

        {bookmark.summary && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md px-3 py-2 mt-2">
            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">{bookmark.summary}</p>
          </div>
        )}

        {(hasCategories || hasTags) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {hasCategories && bookmark.categories.slice(0, 4).map(cat => (
              <span key={cat} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-xs font-medium">{cat}</span>
            ))}
            {hasTags && bookmark.tags.slice(0, 6).map(t => (
              <span key={t} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">{t}</span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

export default SharedCollection;
